import { FolderIcon, TrashSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { utilities } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { useId, useState } from 'react'
import { useToastService } from '../../services/toast-service.tsx'
import { useDeleteProject } from '../../utils/api-client/api-hooks.ts'
import type { Project } from '../../utils/api-client/api-schemas.ts'
import type { AvailableTemplate } from '../../utils/api-client/templates.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { TextInput } from '../input/text-input.tsx'
import { useShowProjectMutationError } from '../projects/admin-required-override.ts'
import { outlinedGhost } from '../projects/project-dialog.css.ts'
import * as styles from './confirm-dialog.css.ts'

export type DeleteProjectModalProps = {
  project: Pick<Project, 'id' | 'name' | 'repository_count' | '_links'>
  template: AvailableTemplate
  onClose: () => unknown
  onDeleted: () => unknown
  /**
   * Returns focus to whatever opened this dialog. Radix restores to a `Dialog.Trigger`, and this
   * dialog has none — it mounts already open. @see useReturnFocus
   */
  onCloseAutoFocus?: (event: Event) => void
}

/** Confirms deleting a project: its project-scoped feedback goes with it, its repositories stay connected. */
export const DeleteProjectModal: React.FC<DeleteProjectModalProps> = ({
  project,
  template,
  onClose,
  onDeleted,
  onCloseAutoFocus,
}) => {
  const { addToast } = useToastService()
  const [typedName, setTypedName] = useState('')
  const confirmInputId = useId()
  const showError = useShowProjectMutationError('Failed to delete project')
  const deleteMutation = useDeleteProject({
    onSuccess: () => {
      addToast({ message: <>Project &ldquo;{project.name}&rdquo; deleted</>, variant: 'success' })
      onDeleted()
    },
    onError: showError,
  })
  const isPending = deleteMutation.isPending
  const count = project.repository_count

  // The destructive act is gated on retyping the name: the reader has to have read which project
  // this is before the button will fire.
  const isNameConfirmed = typedName === project.name
  const isDeleteDisabled = !isNameConfirmed || isPending

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className={styles.title}>
            <WarningCircleIcon size={32} weight="fill" className={styles.destructiveIcon} aria-hidden />
            Delete project?
          </Dialog.Title>
          <div className={styles.subjectCard}>
            <span className={styles.subjectKind}>
              <FolderIcon size={20} weight="light" aria-hidden />
              <span>Project</span>
            </span>
            <p className={styles.subjectName}>{project.name}</p>
          </div>
          <Dialog.Description asChild>
            <p className={styles.description}>
              {/* The feedback clause carries no number: a count here would have to reconcile
                  platform rows against analysis-service memories, and the spec declines that. */}
              All feedback left at this project&apos;s scope is deleted with it
              {count > 0 && (
                <>
                  , and its {count} {pluralize(count, 'repository becomes', 'repositories become')} ungrouped
                </>
              )}
              . {count > 0 && <>The {pluralize(count, 'repository stays', 'repositories stay')} connected to Pixee. </>}
              This cannot be undone.
            </p>
          </Dialog.Description>
          <TextInput
            size="small"
            id={confirmInputId}
            label={`Type “${project.name}” below to confirm`}
            value={typedName}
            onChange={setTypedName}
            placeholder={project.name}
            autoComplete="off"
          />
          {/* The label says what unlocks the button; this says when it has. A natively disabled button is
              skipped by Tab, so without it nothing tells a screen reader the name now matches. */}
          <span className={utilities.visuallyHidden} role="status">
            {isNameConfirmed ? 'Delete project is available.' : ''}
          </span>
          <div className={styles.buttons}>
            <GhostButton
              type="secondary"
              size="small"
              className={outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              size="small"
              state={isPending ? 'loading' : isDeleteDisabled ? 'disabled' : 'default'}
              onClick={() =>
                deleteMutation.mutate({
                  projectId: project.id,
                  membershipsHref: project._links.memberships.href,
                  template,
                })
              }
              loadingLabel="Deleting project"
              icon={<TrashSimpleIcon size={14} aria-hidden />}
            >
              Delete project
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
